import { Button, Col, DatePicker, Form, Row, Select, Typography, message } from 'antd';
import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { filterOption } from 'app/shared/util/select-filter';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import { changeTimeEffective, createEntity, getYearPlanningWindowDetail, getYearPlanningWindowOptions } from '../annual-plan-window.reducer';

const { Title } = Typography;

type AnnualPlanWindowFormValues = {
  year: number;
  initialStartDate?: Dayjs;
  initialEndDate?: Dayjs;
  adjustmentStartDate?: Dayjs;
  adjustmentEndDate?: Dayjs;
};

const fallbackYearOptions = Array.from({ length: 2050 - 2025 + 1 }, (_, i) => 2025 + i).map(year => ({
  value: year,
  label: String(year),
}));

const normalizeStartOfDay = (value?: Dayjs) => (value ? value.startOf('day').format('YYYY-MM-DD[T]HH:mm:ss') : undefined);
const normalizeEndOfDay = (value?: Dayjs) => (value ? value.endOf('day').format('YYYY-MM-DD[T]HH:mm:ss') : undefined);

const toFormValues = (year: number, entity?: any): AnnualPlanWindowFormValues => ({
  year,
  initialStartDate: entity?.planningStartFrom ? dayjs(entity.planningStartFrom) : undefined,
  initialEndDate: entity?.planningEndAt ? dayjs(entity.planningEndAt) : undefined,
  adjustmentStartDate: entity?.revisionStartFrom ? dayjs(entity.revisionStartFrom) : undefined,
  adjustmentEndDate: entity?.revisionEndAt ? dayjs(entity.revisionEndAt) : undefined,
});

export const AnnualPlanWindow = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<AnnualPlanWindowFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const yearOptions = useAppSelector(state => state.yearPlanningWindow.options);
  const entity = useAppSelector(state => state.yearPlanningWindow.entity);
  const loading = useAppSelector(state => state.yearPlanningWindow.loading);
  const updating = useAppSelector(state => state.yearPlanningWindow.updating);
  const updateSuccess = useAppSelector(state => state.yearPlanningWindow.updateSuccess);
  const mergedYearOptions = useMemo(() => {
    const apiOptions = yearOptions || [];
    const apiYears = new Set(apiOptions.map(option => Number(option.value)).filter(year => !Number.isNaN(year)));
    const options = [...apiOptions, ...fallbackYearOptions.filter(option => !apiYears.has(Number(option.value)))];
    const uniqueOptions = new Map<number, { value: number; label: string }>();

    options.forEach(option => {
      const year = Number(option.value);
      if (!Number.isNaN(year) && !uniqueOptions.has(year)) {
        uniqueOptions.set(year, {
          value: year,
          label: String(option.label ?? year),
        });
      }
    });

    return Array.from(uniqueOptions.values());
  }, [yearOptions]);

  useEffect(() => {
    dispatch(getYearPlanningWindowOptions());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getYearPlanningWindowDetail(selectedYear));
  }, [dispatch, selectedYear]);

  useEffect(() => {
    if (updateSuccess) {
      messageApi.success('Đã lưu cửa sổ lập kế hoạch năm');
      dispatch(getYearPlanningWindowOptions());
      dispatch(getYearPlanningWindowDetail(selectedYear));
    }
  }, [dispatch, entity?.id, messageApi, selectedYear, updateSuccess]);

  useEffect(() => {
    form.setFieldsValue(toFormValues(selectedYear, entity));
  }, [entity, form, selectedYear]);

  const validateDateRange = (startField: keyof AnnualPlanWindowFormValues, endField: keyof AnnualPlanWindowFormValues, messageText: string) => ({
    validator: async (_rule, value: Dayjs | undefined) => {
      const startValue = form.getFieldValue(startField) as Dayjs | undefined;
      const endValue = endField === startField ? value : (form.getFieldValue(endField) as Dayjs | undefined);

      if (!startValue || !endValue || !dayjs(startValue).isAfter(endValue, 'day')) {
        return Promise.resolve();
      }

      return Promise.reject(new Error(messageText));
    },
  });

  const handleChangeYear = (year: number) => {
    setSelectedYear(year);
  };

  const handleSubmit = (values: AnnualPlanWindowFormValues) => {
    const payload = {
      planningStartFrom: normalizeStartOfDay(values.initialStartDate),
      planningEndAt: normalizeEndOfDay(values.initialEndDate),
      revisionStartFrom: normalizeStartOfDay(values.adjustmentStartDate),
      revisionEndAt: normalizeEndOfDay(values.adjustmentEndDate),
    };

    if (entity?.id) {
      dispatch(
        changeTimeEffective({
          id: entity.id,
          ...payload,
        }),
      );
      return;
    }

    dispatch(
      createEntity({
        year: values.year,
        ...payload,
      }),
    );
  };

  return (
    <Container className="form">
      {contextHolder}
      <Form
        size="large"
        form={form}
        layout="vertical"
        initialValues={{ year: selectedYear }}
        onFinish={handleSubmit}
      >
        <Title level={3}>Cửa sổ lập kế hoạch năm</Title>
        <Row>
          <Col md={8}>
            <Form.Item label="Năm kế hoạch" name="year" rules={[{ required: true, message: 'Hãy chọn năm kế hoạch' }]}>
              <Select
                options={mergedYearOptions}
                onChange={handleChangeYear}
                filterOption={filterOption}
                loading={loading && mergedYearOptions.length === 0}
                listHeight={5 * 32}
                placeholder="Chọn năm kế hoạch"
                showSearch
              />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>Thời gian lập kế hoạch ban đầu</Title>
        <Row>
          <Col md={11}>
            <Form.Item
              label="Từ ngày"
              name="initialStartDate"
              rules={[{ required: true, message: 'Hãy chọn ngày bắt đầu' }, validateDateRange('initialStartDate', 'initialEndDate', 'Từ ngày phải nhỏ hơn hoặc bằng đến ngày')]}
            >
              <DatePicker className="w-100" format={APP_LOCAL_DATE_FORMAT} placeholder="Chọn ngày bắt đầu" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item
              label="Đến ngày"
              name="initialEndDate"
              dependencies={['initialStartDate']}
              rules={[{ required: true, message: 'Hãy chọn ngày kết thúc' }, validateDateRange('initialStartDate', 'initialEndDate', 'Đến ngày phải lớn hơn hoặc bằng từ ngày')]}
            >
              <DatePicker className="w-100" format={APP_LOCAL_DATE_FORMAT} placeholder="Chọn ngày kết thúc" />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>Thời gian điều chỉnh kế hoạch năm</Title>
        <Row>
          <Col md={11}>
            <Form.Item
              label="Từ ngày"
              name="adjustmentStartDate"
              rules={[
                { required: true, message: 'Hãy chọn ngày bắt đầu điều chỉnh' },
                validateDateRange('adjustmentStartDate', 'adjustmentEndDate', 'Từ ngày phải nhỏ hơn hoặc bằng đến ngày'),
              ]}
            >
              <DatePicker className="w-100" format={APP_LOCAL_DATE_FORMAT} placeholder="Chọn ngày bắt đầu điều chỉnh" />
            </Form.Item>
          </Col>
          <Col md={1} />
          <Col md={12}>
            <Form.Item
              label="Đến ngày"
              name="adjustmentEndDate"
              dependencies={['adjustmentStartDate']}
              rules={[
                { required: true, message: 'Hãy chọn ngày kết thúc điều chỉnh' },
                validateDateRange('adjustmentStartDate', 'adjustmentEndDate', 'Đến ngày phải lớn hơn hoặc bằng từ ngày'),
              ]}
            >
              <DatePicker className="w-100" format={APP_LOCAL_DATE_FORMAT} placeholder="Chọn ngày kết thúc điều chỉnh" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Row className="action mt-3" justify="end">
            <Col>
              <Button className="primary" htmlType="submit" loading={updating}>
                {entity?.id ? 'Cập nhật' : 'Tạo mới'}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Container>
  );
};

export default AnnualPlanWindow;
